import { useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { deleteCastPhoto, swapCastPhoto } from '../../api';
import { usePhotoUpload } from '../../use-photo-upload';
import type { EnvelopeRejection, PreviewEnvelope } from '../../types';
import { FotoUploadSlot } from '../personen/FotoUploadSlot';
import type { FotoUploadSlotState } from '../personen/FotoUploadSlot';

/**
 * 5a coordination commit 2026-08-06 · `photo_rejected` screen.
 *
 * Fires when the backend rejects a customer photo because more than
 * one face was detected. The customer is pre-payment; the marker in
 * the Schrittleiste is Personen. The screen shows which cast member's
 * which photo failed, names the detected face count, and offers a
 * fresh upload targeting the same slot.
 *
 * ═══ FLOW ══════════════════════════════════════════════════════════
 *
 *   1. Customer sees heading + body ("Auf dem Foto sind {n} Personen
 *      zu sehen. Bitte lade ein Foto hoch, auf dem nur {name} zu
 *      sehen ist.").
 *   2. Customer picks a new file via FotoUploadSlot.
 *   3. On pick — usePhotoUpload runs the two-hop upload (signed URL
 *      + PUT), returning a fresh key.
 *   4. Screen calls `swapCastPhoto(orderId, castKey, { <slot>PhotoKey })`.
 *   5. On success — the server re-runs safety, transitions the order
 *      back into its pre-payment status, and the parent refetches;
 *      BookRoute re-dispatches on the fresh envelope.
 *   6. On failure — inline error + retry button. The picked file
 *      stays in memory so the customer can retry without re-picking.
 *
 * ═══ NO-REJECTION FALLBACK ═════════════════════════════════════════
 *
 * `envelope.intake?.derived?.rejection` can be absent while
 * `envelope.status === 'photo_rejected'` if the coordination is
 * mid-migration (PWA lands the status union first, backend catches
 * up). In that case the screen renders a generic body without cast
 * details — customer still sees the reupload UI, and once the
 * backend populates the field the specific message shows. No crash,
 * no hard error.
 *
 * ═══ COPY ═══════════════════════════════════════════════════════════
 *
 * All strings are PLACEHOLDER pending Sebastian's authorship. See
 * `STRINGS.book.photoRejected` for the register.
 */

const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
const ERROR_CLASS = 'text-sm text-error';

export interface PhotoRejectedScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly onRefetch: () => void;
}

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'uploading' }
  | { readonly kind: 'deleting' }
  | { readonly kind: 'error' }
  | { readonly kind: 'delete-error' };

interface PickedFile {
  readonly file: File;
  readonly previewUrl: string;
}

export function PhotoRejectedScreen({ envelope, onRefetch }: PhotoRejectedScreenProps) {
  const rejection = envelope.intake?.derived?.rejection;
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [picked, setPicked] = useState<PickedFile | null>(null);
  const photoUpload = usePhotoUpload();

  // Sebastian ruling 2026-08-12: composed from STRINGS by `kind` +
  // `{castName}` interpolation. Fallback fires when the wire's
  // rejection payload is absent OR carries an unknown `kind` — the
  // CTA stays live in that case (see the disabled-guard note below).
  const { heading, body } = composeCopy(rejection);

  const slotState: FotoUploadSlotState =
    picked === null ? { kind: 'empty' } : { kind: 'picked', previewUrl: picked.previewUrl };

  const handleFileSelected = (file: File) => {
    if (picked !== null) URL.revokeObjectURL(picked.previewUrl);
    setPicked({ file, previewUrl: URL.createObjectURL(file) });
  };

  const handleRemove = () => {
    if (picked !== null) URL.revokeObjectURL(picked.previewUrl);
    setPicked(null);
    setPhase({ kind: 'idle' });
  };

  const runSwap = async () => {
    if (picked === null || rejection === undefined) return;
    setPhase({ kind: 'uploading' });
    try {
      const [key] = await photoUpload.upload([picked.file]);
      if (key === undefined) {
        throw new Error('swap-photo: upload returned no key');
      }
      // Sebastian ruling 2026-08-12 (Ruling 1): PWA adopts backend
      // `SwapCastPhotoDto` shape verbatim — `{whichPhoto, newPhotoKey}`
      // rather than the legacy `{facePhotoKey}`/`{fullBodyPhotoKey}`
      // alternation. See `pwa/src/features/book/api.ts` JSDoc for the
      // contract history.
      await swapCastPhoto(envelope.bookOrderId, rejection.castKey, {
        whichPhoto: rejection.whichPhoto,
        newPhotoKey: key,
      });
      onRefetch();
    } catch (err) {
      setPhase({ kind: 'error' });

      console.warn('[photo-rejected] swap failed', err);
    }
  };

  /**
   * Delete-photo path (Sebastian ruling 2026-08-12, wave commit 2).
   * Only fired from the "Foto entfernen" button, which renders only
   * when `rejection.whichPhoto === 'fullBody'`. Face slot is mandatory
   * per §A3.2 E-19; the backend rejects a face-delete at the URL with
   * `book_photo_delete_face_forbidden` — defense in depth.
   *
   * On success the backend NULLs the fullBody photo key, CLEARS the
   * rejection payload, and transitions the order back to
   * light_gate_pending. Same refetch discipline as the swap path.
   */
  const runDelete = async () => {
    if (rejection === undefined || rejection.whichPhoto !== 'fullBody') return;
    setPhase({ kind: 'deleting' });
    try {
      await deleteCastPhoto(envelope.bookOrderId, rejection.castKey, 'fullBody');
      onRefetch();
    } catch (err) {
      setPhase({ kind: 'delete-error' });

      console.warn('[photo-rejected] delete failed', err);
    }
  };

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-start gap-4">
        {/*
          Rejected-photo tile — Sebastian ruling 2026-08-12: renders
          the failing photo beside the message so the customer sees
          which photo failed without any wording about "Porträtfoto"
          or "Ganzkörperfoto". Absent (`rejectedPhotoUrl === null` or
          missing) → tile omitted, copy carries all the context.
        */}
        {rejection?.rejectedPhotoUrl != null && (
          <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-dream-navy/10">
            <img
              src={rejection.rejectedPhotoUrl}
              alt={STRINGS.book.photoRejected.rejectedTileAria}
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <h1 className="text-h3 leading-tight text-dream-navy">{heading}</h1>
          <p className="text-base leading-relaxed text-dream-navy">{body}</p>
        </div>
      </div>

      <FotoUploadSlot
        state={slotState}
        onFileSelected={handleFileSelected}
        onRemove={handleRemove}
        showRules={true}
        ariaLabel={STRINGS.book.photoRejected.slotAriaEmpty}
        // Conditional spread — under exactOptionalPropertyTypes an
        // optional `castName?: string` prop cannot accept a
        // possibly-undefined value directly. When the rejection
        // payload carries a castName we pass it; otherwise we omit
        // the prop entirely (which is what "optional" means).
        {...(rejection?.castName !== undefined ? { castName: rejection.castName } : {})}
      />

      {phase.kind === 'uploading' && (
        <p className="text-sm text-dream-navy/70">{STRINGS.book.photoRejected.uploadingHeading}</p>
      )}

      {phase.kind === 'error' && (
        <p className={ERROR_CLASS}>{STRINGS.book.photoRejected.swapErrorPlaceholder}</p>
      )}

      {phase.kind === 'delete-error' && (
        <p className={ERROR_CLASS}>{STRINGS.book.photoRejected.deleteErrorPlaceholder}</p>
      )}

      {/*
        Sebastian ruling 2026-08-12: the CTA MUST NOT depend on the
        rejection payload (`rejection !== undefined` was the pre-fix
        guard — Layer-3 root cause of Defect 1 in the halt-level
        finding). The rejection object drives the DISPLAY, never the
        FLOW. If a future envelope gap leaves rejection undefined,
        the customer sees the fallback copy but the CTA stays live
        and the swap PATCH still fires against `envelope.bookOrderId`
        (with a null castKey — the backend answers 404 on that path,
        which surfaces as the generic swap-error line rather than a
        dead button). Any future gap surfaces a real message, not a
        silent nothing.
      */}
      <button
        type="button"
        onClick={() => void runSwap()}
        disabled={picked === null || phase.kind === 'uploading' || phase.kind === 'deleting'}
        className={BUTTON_GOLD}
      >
        {phase.kind === 'error'
          ? STRINGS.book.photoRejected.retryCta
          : STRINGS.book.personen.blockWeiterLabel}
      </button>

      {/*
        Delete-photo CTA (Sebastian ruling 2026-08-12, wave commit 2).
        Visible ONLY when the rejected slot is `fullBody` — face is
        mandatory per §A3.2 E-19, cannot be dropped. Sits UNDER the
        primary Weiter CTA as an underlined secondary action so the
        replace-with-a-new-photo path stays the visual default.
      */}
      {rejection?.whichPhoto === 'fullBody' && (
        <button
          type="button"
          onClick={() => void runDelete()}
          disabled={phase.kind === 'uploading' || phase.kind === 'deleting'}
          className="w-full text-center text-sm text-dream-navy underline disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-dream-navy/50"
        >
          {STRINGS.book.photoRejected.deleteFullBodyCta}
        </button>
      )}
    </div>
  );
}

/**
 * Compose the {heading, body} pair from the rejection payload.
 * Case-based on `kind` (Sebastian ruling 2026-08-12 keeps `kind` on
 * the wire so a future `photo_sanitize_failed` surface renders its
 * own Sebastian-authored copy without a code path added here). All
 * text sourced from `STRINGS.book.photoRejected.*` — the customer-
 * text file is the single source of truth per the verbatim rule.
 */
function composeCopy(rejection: EnvelopeRejection | undefined): {
  readonly heading: string;
  readonly body: string;
} {
  if (rejection === undefined) {
    return {
      heading: STRINGS.book.photoRejected.bodyFallback,
      body: '',
    };
  }
  const name = rejection.castName;
  switch (rejection.kind) {
    case 'photo_multi_face':
      return {
        heading: STRINGS.book.photoRejected.multiFaceHeading.replace('{castName}', name),
        body: STRINGS.book.photoRejected.multiFaceBody.replaceAll('{castName}', name),
      };
    case 'heic_convert_failed':
      return {
        heading: STRINGS.book.photoRejected.heicConvertFailedHeading.replace('{castName}', name),
        body: STRINGS.book.photoRejected.heicConvertFailedBody,
      };
    default:
      return {
        heading: STRINGS.book.photoRejected.bodyFallback,
        body: '',
      };
  }
}
